import { Icon } from '@iconify/react/dist/iconify.js';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  getSubscriptions,
  createSubscription,
  updateSubscription
} from '.././helper/Api/AdminApi';

const SubscriptionListLayer = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 25, 50, 'All'];
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    SubscriptionName: '',
    IsDefault: false,
    IsFree: false,
    ActualDurationInMonths: '',
    ExtraMonths: 0,
    UnDiscountedPrice: '',
    SubscriptionFee: '',
    IsActive: true,
    IsBasicPlan: false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchSubscriptions();
  }, []);


  useEffect(() => {
    if (showModal || showConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showModal, showConfirm]);


  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await getSubscriptions();

      let data = [];

      if (Array.isArray(response)) {
        data = response;
      } else if (response?.data) {
        data = response.data;
      } else if (response?.Data) {
        data = response.Data;
      } else if (response?.result) {
        data = response.result;
      }

      setSubscriptions(data || []);

      if (!data || data.length === 0) {
        toast.info("No subscriptions found");
      }
    } catch (error) {
      console.error("Error fetching subscriptions:", error);
      toast.error(error.response?.data?.message || "Failed to fetch subscriptions");
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub =>
    (sub.SubscriptionName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (sub.DurationInMonths?.toString() || '').includes(searchTerm) ||
    (sub.SubscriptionFee?.toString() || '').includes(searchTerm)
  );

  const totalItems = filteredSubscriptions.length;
  const totalPages = Math.ceil(totalItems / (pageSize === 'All' ? totalItems : pageSize));

  const startIndex = pageSize === 'All' ? 0 : (currentPage - 1) * pageSize;
  const endIndex = pageSize === 'All' ? totalItems : currentPage * pageSize;

  const paginatedSubscriptions = pageSize === 'All'
    ? filteredSubscriptions
    : filteredSubscriptions.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const halfVisible = Math.floor(maxVisiblePages / 2);
      let start = currentPage - halfVisible;
      let end = currentPage + halfVisible;

      if (start < 1) {
        start = 1;
        end = maxVisiblePages;
      }

      if (end > totalPages) {
        end = totalPages;
        start = totalPages - maxVisiblePages + 1;
      }

      for (let i = start; i <= end; i++) pages.push(i);
    }

    return pages;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePageChange = (page) => setCurrentPage(page);

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (pageSize === 'All') {
      setCurrentPage(1);
    }
  }, [pageSize]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.SubscriptionName?.trim()) {
      newErrors.SubscriptionName = "Subscription Name is required";
    }

    if (!formData.ActualDurationInMonths) {
      newErrors.ActualDurationInMonths = "Duration is required";
    } else if (!/^\d+$/.test(formData.ActualDurationInMonths)) {
      newErrors.ActualDurationInMonths = "Duration must be a whole number";
    } else if (parseInt(formData.ActualDurationInMonths) <= 0) {
      newErrors.ActualDurationInMonths = "Duration must be greater than 0";
    }

    if (formData.ExtraMonths === '' || formData.ExtraMonths === null) {
      newErrors.ExtraMonths = "Extension is required";
    } else if (!/^\d+$/.test(formData.ExtraMonths)) {
      newErrors.ExtraMonths = "Extension must be a whole number";
    } else if (parseInt(formData.ExtraMonths) < 0) {
      newErrors.ExtraMonths = "Extension cannot be negative";
    }

    if (!formData.UnDiscountedPrice) {
      newErrors.UnDiscountedPrice = "Original Price is required";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.UnDiscountedPrice)) {
      newErrors.UnDiscountedPrice = "Original Price must be a valid number";
    } else if (parseFloat(formData.UnDiscountedPrice) < 0) {
      newErrors.UnDiscountedPrice = "Original Price cannot be negative";
    }

    if (!formData.SubscriptionFee) {
      newErrors.SubscriptionFee = "Discounted Price is required";
    } else if (!/^\d+(\.\d{1,2})?$/.test(formData.SubscriptionFee)) {
      newErrors.SubscriptionFee = "Discounted Price must be a valid number";
    } else if (parseFloat(formData.SubscriptionFee) < 0) {
      newErrors.SubscriptionFee = "Discounted Price cannot be negative";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'IsDefault') {
      const isTurningOnDefault = checked && !formData.IsDefault;

      if (isTurningOnDefault) {
        const existingDefault = subscriptions.find(sub => sub.IsDefault === true || sub.IsDefault === 1);

        if (existingDefault && (!currentSubscription || existingDefault.ID !== currentSubscription.ID)) {
          setConfirmMessage("The record whose Default flag is 1 will be updated to 0. Please click on Continue to proceed.");
          setShowConfirm(true);
          return;
        }
      }

      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleIsActiveChange = (e) => {
    setFormData(prev => ({
      ...prev,
      IsActive: e.target.value === "true"
    }));
  };

  const handleConfirmContinue = () => {
    setFormData(prev => ({
      ...prev,
      IsDefault: true
    }));
    setShowConfirm(false);
    setConfirmMessage('');
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check if turning off the only default subscription
    if (currentSubscription && !formData.IsDefault) {
      const defaultSubscriptions = subscriptions.filter(sub =>
        (sub.IsDefault === true || sub.IsDefault === 1) && sub.ID !== currentSubscription.ID
      );
      if (defaultSubscriptions.length === 0) {
        toast.error("At least one subscription must be set as default!");
        return;
      }
    }

    if (formData.IsBasicPlan) {
      const existingBasic = subscriptions.find(sub =>
        (sub.IsBasicPlan === true || sub.IsBasicPlan === 1) &&
        (!currentSubscription || sub.ID !== currentSubscription.ID)
      );
      if (existingBasic) {
        toast.error("Only one subscription plan can have IsBasic = true");
        return;
      }
    }

    setLoading(true);
    try {
      const actualDuration = parseInt(formData.ActualDurationInMonths, 10);
      const extraMonths = parseInt(formData.ExtraMonths, 10);

      const subscriptionData = {
        SubscriptionName: formData.SubscriptionName,
        IsDefault: formData.IsDefault,
        IsFree: formData.IsFree,
        ActualDurationInMonths: actualDuration,
        ExtraMonths: extraMonths,
        DurationInMonths: actualDuration + extraMonths,
        UnDiscountedPrice: parseFloat(formData.UnDiscountedPrice),
        SubscriptionFee: parseFloat(formData.SubscriptionFee),
        IsActive: formData.IsActive,
        IsBasicPlan: formData.IsBasicPlan,
      };

      if (currentSubscription) {
        subscriptionData.ID = currentSubscription.ID;

        const response = await updateSubscription(currentSubscription.ID, subscriptionData);

        if (formData.IsDefault) {
          const otherSubscriptions = subscriptions.filter(sub =>
            (sub.IsDefault === true || sub.IsDefault === 1) && sub.ID !== currentSubscription.ID
          );

          setSubscriptions(prev => prev.map(sub =>
            sub.ID === currentSubscription.ID
              ? { ...sub, ...subscriptionData }
              : { ...sub, IsDefault: false }
          ));

          for (const sub of otherSubscriptions) {
            try {
              await updateSubscription(sub.ID, {
                ...sub,
                IsDefault: false
              });
            } catch (err) {
              console.error("Error updating other subscription:", err);
            }
          }
        } else {
          setSubscriptions(prev => prev.map(sub =>
            sub.ID === currentSubscription.ID
              ? { ...sub, ...subscriptionData }
              : sub
          ));
        }

        toast.success(response.message || response.Message || "Subscription updated successfully!");
      } else {

        const response = await createSubscription(subscriptionData);

        if (formData.IsDefault) {
          const otherSubscriptions = subscriptions.filter(sub => sub.IsDefault === true || sub.IsDefault === 1);

          for (const sub of otherSubscriptions) {
            try {
              await updateSubscription(sub.ID, {
                ...sub,
                IsDefault: false
              });
            } catch (err) {
              console.error("Error updating other subscription:", err);
            }
          }
        }

        // Refresh the subscriptions list to get the correct ID from the server
        await fetchSubscriptions();

        toast.success(response.message || response.Message || "Subscription created successfully!");
      }

      handleCloseModal();
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.message || error.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (subscription) => {
    setCurrentSubscription(subscription);
    setFormData({
      SubscriptionName: subscription.SubscriptionName || '',
      IsDefault: subscription.IsDefault || subscription.IsDefault === 1 ? true : false,
      IsFree: subscription.IsFree || subscription.IsFree === 1 ? true : false,
      ActualDurationInMonths: subscription.ActualDurationInMonths?.toString() || subscription.DurationInMonths?.toString() || '',
      ExtraMonths: subscription.ExtraMonths?.toString() || '0',
      UnDiscountedPrice: subscription.UnDiscountedPrice?.toString() || '',
      SubscriptionFee: subscription.SubscriptionFee?.toString() || '',
      IsActive: subscription.IsActive !== undefined ? (subscription.IsActive === true || subscription.IsActive === 1) : true,
      IsBasicPlan: subscription.IsBasicPlan || subscription.IsBasicPlan === 1 ? true : false,
    });
    setShowModal(true);
  };

  const handleAddNew = () => {
    setCurrentSubscription(null);
    setFormData({
      SubscriptionName: '',
      IsDefault: false,
      IsFree: false,
      ActualDurationInMonths: '',
      ExtraMonths: 0,
      UnDiscountedPrice: '',
      SubscriptionFee: '',
      IsActive: true,
      IsBasicPlan: false,
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentSubscription(null);
    setErrors({});
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-sm text-secondary-light">Show:</span>
            <div className="dropdown">
              <button
                className="btn btn-outline-secondary btn-sm dropdown-toggle py-2 px-3 radius-6"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                disabled={loading}
              >
                {pageSize === 'All' ? 'All' : pageSize}
              </button>
              <ul className="dropdown-menu shadow-sm border-0 radius-8 py-2">
                {pageSizeOptions.map((option) => (
                  <li key={option}>
                    <button
                      className={`dropdown-item py-2 px-3 text-sm ${pageSize === option ? 'active bg-primary-50 text-primary-600' : ''}`}
                      onClick={() => handlePageSizeChange(option)}
                      disabled={loading}
                    >
                      {option}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <form className="navbar-search">
            <input
              type="text"
              className="form-control radius-8 h-40-px w-auto"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              disabled={loading}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={handleAddNew}
            disabled={loading}
          >
            <Icon icon="ic:baseline-plus" className="icon text-lg line-height-1" />
            Add New Subscription
          </button>
        </div>
      </div>

      <div className="card-body p-24">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading subscriptions...</p>
          </div>
        ) : (
          <>
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead>
                  <tr>
                    <th scope="col" className="text-center">Action</th>
                    <th scope="col">Subscription Name</th>
                    <th scope="col" className="text-center">Default</th>
                    <th scope="col" className="text-center">Free</th>
                    <th scope="col" className="text-center">Duration (Months)</th>
                    <th scope="col" className="text-center">Fee</th>
                    <th scope="col" className="text-center">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscriptions.length > 0 ? (
                    paginatedSubscriptions.map((sub) => (
                      <tr key={sub.ID || sub.id}>
                        <td className="text-center">
                          <div className="d-flex align-items-center gap-10 justify-content-center">
                            <button
                              type="button"
                              className="bg-primary-50 text-primary-600 bg-hover-primary-100 fw-medium w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle"
                              onClick={() => handleEdit(sub)}
                              disabled={loading}
                            >
                              <Icon icon="lucide:edit" className="icon text-lg" />
                            </button>
                          </div>
                        </td>
                        <td>{sub.SubscriptionName || sub.subscriptionName || 'N/A'}</td>
                        <td className="text-center">
                          {(sub.IsDefault === true || sub.IsDefault === 1) ? (
                            <Icon icon="mdi:check-circle" className="text-success text-lg" />
                          ) : (
                            <Icon icon="mdi:close-circle" className="text-secondary text-lg" />
                          )}
                        </td>
                        <td className="text-center">
                          {(sub.IsFree === true || sub.IsFree === 1) ? (
                            <Icon icon="mdi:check-circle" className="text-success text-lg" />
                          ) : (
                            <Icon icon="mdi:close-circle" className="text-secondary text-lg" />
                          )}
                        </td>
                        <td className="text-center">{sub.DurationInMonths || 'N/A'}</td>
                        <td className="text-center">{sub.SubscriptionFee || 'N/A'}</td>
                        <td className="text-center">
                          {(sub.IsActive === true || sub.IsActive === 1) ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">Inactive</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center py-4">
                        {subscriptions.length === 0 ? (
                          <div>
                            <p>No subscriptions available</p>
                            <button
                              className="btn btn-sm btn-primary mt-2"
                              onClick={handleAddNew}
                            >
                              Add your first subscription
                            </button>
                          </div>
                        ) : (
                          "No subscriptions match your search"
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
              <div className="d-flex align-items-center gap-3">
                <span className="text-secondary-light text-sm">
                  Showing {pageSize === 'All' ? 1 : Math.min(startIndex + 1, totalItems)} to{" "}
                  {Math.min(endIndex, totalItems)} of {totalItems} entries
                </span>
              </div>

              {pageSize !== 'All' && totalPages > 1 && (
                <ul className="pagination d-flex flex-wrap align-items-center gap-2 m-0 p-0">
                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      style={{ opacity: currentPage === 1 ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-left" className="text-lg" />
                    </button>
                  </li>

                  {getPageNumbers().map((page) => (
                    <li className="page-item" key={page}>
                      <button
                        className={`page-link radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px fw-medium text-sm transition-all ${currentPage === page
                            ? "bg-primary-600 text-white shadow-sm"
                            : "bg-neutral-100 text-secondary-light hover:bg-neutral-200"
                          }`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  <li className="page-item">
                    <button
                      className="page-link bg-neutral-100 text-secondary-light radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      style={{ opacity: currentPage === totalPages ? 0.4 : 1 }}
                    >
                      <Icon icon="ep:d-arrow-right" className="text-lg" />
                    </button>
                  </li>
                </ul>
              )}
            </div>
          </>
        )}
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content radius-12">

              <div className="modal-header">
                <h4 className="modal-title">
                  {currentSubscription ? "Edit Subscription" : "Add New Subscription"}
                </h4>
                <button className="btn-close" disabled={loading} onClick={handleCloseModal}></button>
              </div>

              <div className="modal-body">
                <div className="mb-4">
                  <label className="form-label">
                    Subscription Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.SubscriptionName ? 'is-invalid' : ''}`}
                    name="SubscriptionName"
                    value={formData.SubscriptionName}
                    onChange={handleInputChange}
                    placeholder="Enter Subscription Name"
                    disabled={loading}
                  />
                  {errors.SubscriptionName && <small className="text-danger">{errors.SubscriptionName}</small>}
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="IsDefault"
                        id="IsDefault"
                        checked={formData.IsDefault}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label ms-2" htmlFor="IsDefault">
                        Is Default
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-check d-flex align-items-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="IsFree"
                        id="IsFree"
                        checked={formData.IsFree}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label ms-2" htmlFor="IsFree">
                        Is Free
                      </label>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label">
                      Duration (in Month) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${errors.ActualDurationInMonths ? 'is-invalid' : ''}`}
                      name="ActualDurationInMonths"
                      value={formData.ActualDurationInMonths}
                      onChange={handleInputChange}
                      placeholder="Enter Duration"
                      min="1"
                      disabled={loading}
                    />
                    {errors.ActualDurationInMonths && <small className="text-danger">{errors.ActualDurationInMonths}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Extension (in Month)
                    </label>
                    <input
                      type="number"
                      className={`form-control ${errors.ExtraMonths ? 'is-invalid' : ''}`}
                      name="ExtraMonths"
                      value={formData.ExtraMonths}
                      onChange={handleInputChange}
                      placeholder="Enter Extension"
                      min="0"
                      disabled={loading}
                    />
                    {errors.ExtraMonths && <small className="text-danger">{errors.ExtraMonths}</small>}
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label">
                      Original Price <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${errors.UnDiscountedPrice ? 'is-invalid' : ''}`}
                      name="UnDiscountedPrice"
                      value={formData.UnDiscountedPrice}
                      onChange={handleInputChange}
                      placeholder="Enter Original Price"
                      min="0"
                      disabled={loading}
                    />
                    {errors.UnDiscountedPrice && <small className="text-danger">{errors.UnDiscountedPrice}</small>}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Discounted Price <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className={`form-control ${errors.SubscriptionFee ? 'is-invalid' : ''}`}
                      name="SubscriptionFee"
                      value={formData.SubscriptionFee}
                      onChange={handleInputChange}
                      placeholder="Enter Discounted Price"
                      min="0"
                      disabled={loading}
                    />
                    {errors.SubscriptionFee && <small className="text-danger">{errors.SubscriptionFee}</small>}
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label">Is Active</label>
                    <div className="d-flex gap-4 mt-2">
                      <label className="form-check d-flex align-items-center">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="IsActive"
                          value="true"
                          checked={formData.IsActive === true}
                          onChange={handleIsActiveChange}
                          disabled={loading}
                        />
                        <span className="ms-2">Yes</span>
                      </label>

                      <label className="form-check d-flex align-items-center">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="IsActive"
                          value="false"
                          checked={formData.IsActive === false}
                          onChange={handleIsActiveChange}
                          disabled={loading}
                        />
                        <span className="ms-2">No</span>
                      </label>
                    </div>
                  </div>
                  <div className="col-md-6 d-flex align-items-center">
                    <div className="form-check d-flex align-items-center mt-4">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        name="IsBasicPlan"
                        id="IsBasicPlan"
                        checked={formData.IsBasicPlan}
                        onChange={handleInputChange}
                        disabled={loading}
                      />
                      <label className="form-check-label ms-2" htmlFor="IsBasicPlan">
                        Is this a Basic Plan
                      </label>
                    </div>
                  </div>
                </div>

                <div className="d-flex justify-content-center gap-3 mt-4">
                  <button
                    className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-10 radius-8"
                    disabled={loading}
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-primary px-56 py-10"
                    disabled={loading}
                    onClick={handleSave}
                  >
                    {loading ? "Processing..." : (currentSubscription ? "Update" : "Save")}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content border-0 radius-12 shadow-lg p-24">
              <h6 className="text-center fw-semibold mb-12">Are you sure?</h6>
              <p className="text-center text-secondary-light mb-20" style={{ fontSize: "14px" }}>
                {confirmMessage}
              </p>
              <div className="d-flex justify-content-center gap-2">
                <button
                  className="btn btn-light px-20 py-6 radius-8"
                  onClick={() => {
                    setShowConfirm(false);
                    setConfirmMessage('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary px-20 py-6 radius-8"
                  onClick={handleConfirmContinue}
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionListLayer;